/**
 * Where each room sits on the 3D floor view (z8nrz7fw25).
 *
 * The app knows a room's area but not its shape or where it is on the floor,
 * so the view is a generated layout, not a floor plan: each room is a block
 * sized from its area, and blocks are packed in rows, left to right, with a
 * one-cell corridor between them. Pure, so the same floor always draws the
 * same way and the rule is tested.
 */

export interface IsoRoomInput {
  id: string
  areaSqm?: number | null
  /** Units in the room: a room with no recorded area grows to hold them. */
  unitCount: number
  /** Where someone dragged it, in cells. Kept as placed; the rest are packed around it. */
  plan?: { x: number; y: number } | null
  /** Its size set by hand, in cells; wins over the area. */
  size?: { w: number; d: number } | null
}

export interface IsoRoomBox {
  id: string
  /** Grid cells from the floor's back-left corner. */
  x: number
  y: number
  /** Width (across) and depth (back to front), in cells. */
  w: number
  d: number
}

/** One cell is roughly this many square metres of floor. */
const SQM_PER_CELL = 4
const MIN_SIDE = 3
const MAX_W = 7
const MAX_D = 5

/** A room's footprint in cells: from its area when recorded, else from what is in it. */
export function roomFootprint(room: Pick<IsoRoomInput, 'areaSqm' | 'unitCount' | 'size'>): { w: number; d: number } {
  if (room.size) return { w: room.size.w, d: room.size.d }
  const cells =
    room.areaSqm && room.areaSqm > 0
      ? Math.round(room.areaSqm / SQM_PER_CELL)
      : Math.max(MIN_SIDE * MIN_SIDE, (room.unitCount + 2) * 2)
  const w = Math.min(MAX_W, Math.max(MIN_SIDE, Math.ceil(Math.sqrt(cells * 1.4))))
  const d = Math.min(MAX_D, Math.max(MIN_SIDE, Math.ceil(cells / w)))
  return { w, d }
}

/**
 * Pack a floor's rooms into rows `width` cells wide, keeping their order. The
 * plane is one cell bigger than the rooms on every side: the margin is the
 * corridor around them.
 */
export function layoutFloor(rooms: IsoRoomInput[], width = 16): { boxes: IsoRoomBox[]; width: number; depth: number } {
  // Rooms someone placed stay exactly where they were put.
  const placed: IsoRoomBox[] = rooms
    .filter((r) => r.plan)
    .map((r) => ({ id: r.id, x: r.plan!.x, y: r.plan!.y, ...roomFootprint(r) }))
  const boxes: IsoRoomBox[] = []
  let x = 1
  let y = 1
  let rowDepth = 0
  for (const room of rooms) {
    if (room.plan) continue
    const { w, d } = roomFootprint(room)
    // Next free spot in reading order: wrap at the edge, step past placed rooms.
    for (let guard = 0; guard < 500; guard++) {
      if (x > 1 && x + w > width - 1) {
        x = 1
        y += rowDepth + 1
        rowDepth = 0
      }
      const clash = placed.find((p) => overlaps({ x, y, w, d }, p))
      if (!clash) break
      x = clash.x + clash.w + 1
      rowDepth = Math.max(rowDepth, d)
    }
    const box = { id: room.id, x, y, w, d }
    boxes.push(box)
    placed.push(box)
    x += w + 1
    rowDepth = Math.max(rowDepth, d)
  }
  const all = rooms.map((r) => placed.find((b) => b.id === r.id) as IsoRoomBox)
  return {
    boxes: all,
    width: Math.max(width, ...all.map((b) => b.x + b.w + 1)),
    depth: all.length === 0 ? 6 : Math.max(...all.map((b) => b.y + b.d + 1)),
  }
}

/** Whether two rooms touch: a one-cell corridor must stay between them. */
export function overlaps(a: Pick<IsoRoomBox, 'x' | 'y' | 'w' | 'd'>, b: Pick<IsoRoomBox, 'x' | 'y' | 'w' | 'd'>): boolean {
  return !(a.x + a.w + 1 <= b.x || b.x + b.w + 1 <= a.x || a.y + a.d + 1 <= b.y || b.y + b.d + 1 <= a.y)
}

/** Whether a room can be dropped at (x, y): on the floor, and clear of every other room. */
export function canDrop(box: IsoRoomBox, x: number, y: number, others: IsoRoomBox[]): boolean {
  if (x < 0 || y < 0) return false
  const moved = { ...box, x, y }
  return others.every((o) => o.id === box.id || !overlaps(moved, o))
}

/** The camera on a 3D floor: tipped back by `tilt`, turned by `turn` (degrees), scaled by `zoom`. */
export interface IsoCamera {
  tilt: number
  turn: number
  zoom: number
  panX: number
  panY: number
}

export const DEFAULT_CAMERA: IsoCamera = { tilt: 56, turn: -38, zoom: 1, panX: 0, panY: 0 }

/**
 * A drag on screen, in pixels, as a move across the floor, in pixels of the
 * floor's own grid. Undoes the camera in reverse: the zoom, then the tilt
 * (which squashes the floor's depth by cos(tilt)), then the turn. Perspective
 * is ignored, which is close enough to follow the pointer.
 */
export function screenToPlane(dx: number, dy: number, camera: Pick<IsoCamera, 'tilt' | 'turn' | 'zoom'>): { x: number; y: number } {
  const sx = dx / camera.zoom
  const sy = dy / camera.zoom / Math.max(0.15, Math.cos((camera.tilt * Math.PI) / 180))
  const t = (-camera.turn * Math.PI) / 180
  return { x: sx * Math.cos(t) - sy * Math.sin(t), y: sx * Math.sin(t) + sy * Math.cos(t) }
}

/**
 * Where a point of the floor lands on screen, relative to the floor's centre:
 * the camera's turn, then its tilt (depth squashed by cos, height lifted by
 * sin), then its zoom. `z` is height above the plane, e.g. a storey. Used to
 * pan a clicked room into the middle of the view.
 */
export function planeToScreen(x: number, y: number, z: number, camera: Pick<IsoCamera, 'tilt' | 'turn' | 'zoom'>): { x: number; y: number } {
  const t = (camera.turn * Math.PI) / 180
  const rx = x * Math.cos(t) - y * Math.sin(t)
  const ry = x * Math.sin(t) + y * Math.cos(t)
  const tilt = (camera.tilt * Math.PI) / 180
  return { x: rx * camera.zoom, y: (ry * Math.cos(tilt) - z * Math.sin(tilt)) * camera.zoom }
}

/** The camera that brings a point to the middle of the view at a closer zoom, keeping the angle. */
export function focusCamera(camera: IsoCamera, target: { x: number; y: number; z: number }, zoom = 1.8): IsoCamera {
  const z = Math.max(camera.zoom, zoom)
  const at = planeToScreen(target.x, target.y, target.z, { ...camera, zoom: z })
  return { ...camera, zoom: z, panX: -at.x, panY: -at.y }
}

/** A floor dragged up or down the building by some storeys: the floors' new order, top storey last. */
export function moveFloor<T extends { id: string; level: number }>(floors: T[], floorId: string, steps: number): T[] {
  const order = [...floors].sort((a, b) => a.level - b.level)
  const from = order.findIndex((f) => f.id === floorId)
  if (from < 0 || steps === 0) return order
  const to = Math.min(order.length - 1, Math.max(0, from + steps))
  const [moved] = order.splice(from, 1)
  order.splice(to, 0, moved)
  return order
}
